import {
  App,
  Divider,
  Dropdown,
  Space,
  Typography,
} from 'antd'
import React, {
  useCallback,
  useMemo,
} from 'react'

import FunctionPermission from '@/components/permissions/function-permission'
import { FunctionKey } from '@/constants/function-key'
import { NormalStatus } from '@/constants/normal-status'
import {
  activateCreator,
  deactivateCreator,
  removeCreator,
} from '@/redux/creator-actions'
import { selectMineFunctionKeys } from '@/redux/function-permission-selector'
import {
  useAppDispatch,
  useAppSelector,
} from '@/redux/utils'

import type { Record } from '../hooks/use-creator-columns'

const { Link } = Typography

export interface CreatorOperationsProps {
  creator: Record
  openUpdateFormModal: (creator: Record) => void
}

export default function CreatorOperations({
  creator,
  openUpdateFormModal,
}: CreatorOperationsProps) {
  const { modal, message } = App.useApp()
  const dispatch = useAppDispatch()
  const openModal = useCallback(() => openUpdateFormModal(creator), [openUpdateFormModal, creator])
  const mineFunctionKeys = useAppSelector(selectMineFunctionKeys)
  const dropdownMenuItems = useMemo(() => {
    const items = []
    if (mineFunctionKeys.includes(FunctionKey.CreatorsManageEnableDisableCreator)) {
      items.push({
        key: creator.status === NormalStatus.Activated ? 'deactivate' : 'activate',
        label: creator.status === NormalStatus.Activated ? 'Deactivate' : 'Activate',
        onClick: () => {
          if (creator.status === NormalStatus.Activated) {
            void modal.confirm({
              icon: null,
              closeIcon: false,
              maskClosable: true,
              title: 'Deactivate Creator',
              content: 'When you deactivate a Creator, your account will still be charged. To avoid unwanted fees, it\'s important to delete the account.',
              okText: 'Deactivate',
              onOk: async () => {
                await dispatch(deactivateCreator(creator.id)).unwrap()
                void message.success('Deactivate success')
              },
            })
            return
          }
          void modal.confirm({
            icon: null,
            closeIcon: false,
            maskClosable: true,
            title: 'Activate Creator',
            content: `By reactivating ${creator.name}, employees will regain access to the account and its associated data`,
            okText: 'Activate',
            onOk: async () => {
              await dispatch(activateCreator(creator.id)).unwrap()
              void message.success('Activate success')
            },
          })
        },
      })
    }
    if (mineFunctionKeys.includes(FunctionKey.CreatorsManageDeleteCreator)) {
      items.push({
        key: 'delete',
        label: 'Delete',
        danger: true,
        onClick: () => {
          void modal.confirm({
            icon: null,
            closeIcon: false,
            maskClosable: true,
            title: 'Delete Creator',
            okType: 'danger',
            okText: 'Delete',
            content: `Creator ${creator.name} will be removed. Any data related to this creator will be deleted and cannot be recovered.`,
            onOk: async () => {
              await dispatch(removeCreator(creator.id)).unwrap()
              void message.success('Delete success')
            },
          })
        },
      })
    }
    return items
  }, [creator, dispatch, message, mineFunctionKeys, modal])

  return (
    <Space split={<Divider type="vertical" />}>
      <FunctionPermission functionKey={FunctionKey.CreatorsManageEditCreator}>
        <Link
          onClick={openModal}
          disabled={creator.status === NormalStatus.Deactivated}
        >
          Edit
        </Link>
      </FunctionPermission>
      {dropdownMenuItems.length > 0 && (
        <Dropdown
          menu={{
            items: dropdownMenuItems,
          }}
        >
          <Link>More</Link>
        </Dropdown>
      )}
    </Space>
  )
}
